import React, { useState, memo, useContext, useEffect, useCallback } from 'react';
import { Menu } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import Dashboard from '@/pages/dashboard/components/LeftMenu/components/Menu/Dashboard';
import classNames from 'classnames';
import {
  MENU_OPTION_TYPE_FOLDER,
  MENU_OPTION_TYPE_SUB_FOLDER,
  MenuContext,
} from '@/pages/dashboard/components/LeftMenu/components/Menu/config';

type ISubMenuProps = {
  parent?: any;
  folder: any;
  level?: number;
  menuOptionInfo: React.ComponentState;
  // 打开右侧操作弹窗
  openMenuOption: (
    type: string,
    menu: any,
    ele: EventTarget,
    parentMenu?: any,
  ) => void;
  showNewMenusMap: any;
};

const Folder: React.FC<ISubMenuProps> = (props) => {
  const {
    parent,
    folder,
    level = 0,
    menuOptionInfo,
    openMenuOption,
    showNewMenusMap,
  } = props;
  const { isDefaultOpen, onclick = '', text = '', icon = '' } = folder;

  const [open, setOpen] = useState<boolean>(!!isDefaultOpen);

  const showOption = onclick === 'true';
  const isSubFolder = level > 0;

  const { showBubble, hideBubble } = useContext(MenuContext);
  const folderRef = React.useRef<HTMLLIElement>(null);

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    if (!showBubble || !text) return;

    const titleEl = e.currentTarget.getElementsByClassName('left-nav-title')[0];
    if (titleEl && titleEl.scrollHeight > titleEl.clientHeight) {
      // 锚定浮窗的元素
      const anchor = e.currentTarget.closest('.tea-menu__item');
      showBubble(() => ({ ele: anchor || e.currentTarget, content: text }));
    }
  }, [text, showBubble]);

  const handleMouseLeave = useCallback((): void => {
    hideBubble();
  }, [hideBubble]);

  useEffect(() => {
    const folderEl = folderRef.current;
    if (!folderEl) return;

    const itemEl = folderEl.querySelector('.tea-menu__item');
    if (!itemEl) return;

    // @ts-ignore
    itemEl.addEventListener('mouseenter', handleMouseEnter);
    itemEl.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      // @ts-ignore
      itemEl.removeEventListener('mouseenter', handleMouseEnter);
      itemEl.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseEnter, handleMouseLeave, text]);

  const handleClickOption = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
  ): boolean => {
    e.stopPropagation();
    openMenuOption(
      isSubFolder ? MENU_OPTION_TYPE_SUB_FOLDER : MENU_OPTION_TYPE_FOLDER,
      folder,
      e.target,
      parent,
    );
    return false;
  };

  const showNew = showNewMenusMap.has(folder?.name_en) && !open;

  return (
    <>
      {folder?.dashoboard_list?.length || isSubFolder ? (
        // 有子项的一级目录
        <Menu.SubMenu
          ref={folderRef}
          opened={open}
          title={
            <>
              <div
                className={classNames({
                  'left-nav-hd': true,
                  'left-nav-newhidden': showOption && !isSubFolder,
                  'left-nav-subfolder-newhidden': showOption && isSubFolder,
                })}
              >
                <span className="left-nav-title">{text}</span>
                {showNew ? <span className="left-nav-notice">NEW</span> : ''}
              </div>
              {showOption && (
                <span
                  className={'left-nav-absolute left-nav-mr'}
                  onClick={handleClickOption}
                >
                  <SvgIcon type="more" />
                </span>
              )}
            </>
          }
          onOpenedChange={(isOpen: boolean) => {
            setOpen(isOpen);
          }}
          icon={<SvgIcon className="left-nav-icon" type={icon || 'default'} />}
          className={classNames({
            'dbh-left-nav-ellipsis-with-tooltip': true,
            'left-nav-menu': !isSubFolder,
            'left-nav-subfolder-menu': isSubFolder,
            active:
              menuOptionInfo.visible && menuOptionInfo.menu?.id === folder?.id,
          })}
        >
          {/* 子项 */}
          {(folder?.dashoboard_list || []).map((dashboard: any) => {
            if (dashboard?.menu_type === '3') {
              // 二级目录
              return (
                <Folder
                  showNewMenusMap={showNewMenusMap}
                  parent={folder}
                  level={level + 1}
                  folder={dashboard}
                  key={dashboard?.id}
                  menuOptionInfo={menuOptionInfo}
                  openMenuOption={openMenuOption}
                />
              );
            }
            // 报表
            return (
              <Dashboard
                showNewMenusMap={showNewMenusMap}
                isSubFolder={isSubFolder}
                parent={folder}
                dashboard={dashboard}
                key={dashboard?.id}
                menuOptionInfo={menuOptionInfo}
                openMenuOption={openMenuOption}
              />
            );
          })}
        </Menu.SubMenu>
      ) : (
        // 无报表的一级目录
        <Menu.Item
          // @ts-ignore
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={classNames({
            'left-nav-other dbh-left-nav-ellipsis-with-tooltip': true,
            active:
              menuOptionInfo.visible && menuOptionInfo.menu?.id === folder?.id,
          })}
          icon={<SvgIcon className="left-nav-icon" type={icon || 'default'} />}
          title={
            <>
              <div
                className={classNames({
                  'left-nav-hd': true,
                  'left-nav-newhidden': showOption,
                })}
              >
                <span className="left-nav-title">{text}</span>
              </div>
              {showOption && (
                <span
                  className={'left-nav-absolute'}
                  onClick={handleClickOption}
                >
                  <SvgIcon type="more" />
                </span>
              )}
            </>
          }
        />
      )}
    </>
  );
};

export default memo(Folder);
